/* eslint-disable operator-linebreak */
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import React, { useMemo } from 'react';
import { Dropdown, DropdownProps, Menu } from 'semantic-ui-react';

import Images from '../../../utils/Images';
import { replaceValues } from '../../../utils/stringUtils';
import { useUserCollection } from '../../users/users.hooks';
import { applyDefaultFilter } from '../utils';
import './SelectEntity.scss';

type FilterValue = {
  value: string;
  label: string;
};

type FilterConfig = {
  collection?: string;
  match?: Record<string, any>;
  title: string;
  placeholder?: string;
  dataKey: string;
  labelTemplate?: string;
  imageKey?: string;
  values?: FilterValue[];
};

type SelectEntityProps = {
  value?: string;
  onSelect: (filter: FilterConfig, value: string | undefined) => void;
  filter: FilterConfig;
};

type Entity = {
  _id: string;
  firstName?: string;
  lastName?: string;
  [key: string]: any;
};

type DropdownOption = {
  text: string;
  key: string;
  value: string;
  image?: {
    avatar: boolean;
    src: string;
  };
};

function generateThumbnail(image: any): { avatar: boolean; src: string } | undefined {
  if (!image) return undefined;
  return { avatar: true, src: Images.square(image, 32) };
}

function computeOptions(options: Entity[], filter: FilterConfig): DropdownOption[] {
  const { labelTemplate = '{firstName} {lastName}', imageKey = 'thumbnail' } = filter;
  return orderBy(
    options.map((option) => ({
      text: replaceValues(labelTemplate, option),
      key: option._id,
      value: option._id,
      image: generateThumbnail(option[imageKey]),
    })),
    (o) => o.text.toLowerCase(),
  );
}

const SelectEntity = ({ filter, value, onSelect }: SelectEntityProps): JSX.Element => {
  const { title, placeholder, match, values } = filter;

  const allEntities = useUserCollection(filter.collection || filter.dataKey);

  const finalEntities = useMemo(() => {
    let entities = allEntities;

    if (values && values.length > 0) {
      const valueIdsList = values.map((v) => v.value);
      entities = entities.filter((entity: Entity) => valueIdsList.includes(entity._id));
    }

    if (!isEmpty(match)) {
      entities = entities.filter((item: Entity) => !!applyDefaultFilter(match, item));
    }

    return entities;
  }, [allEntities, match, values]);

  const dropdownOptions = useMemo(() => computeOptions(finalEntities, filter), [
    finalEntities,
    filter,
  ]);

  const handleChange = (e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    onSelect(filter, data.value as string | undefined);
  };

  return (
    <Menu.Item>
      <Menu.Header>{title}</Menu.Header>
      <Dropdown
        className="select-entity--dropdown"
        fluid
        selection
        placeholder={placeholder}
        labeled
        search
        clearable
        selectOnBlur={false}
        options={dropdownOptions}
        value={value ?? ''}
        onChange={handleChange}
      />
    </Menu.Item>
  );
};

SelectEntity.defaultProps = {
  value: undefined,
};

export default SelectEntity;
